import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import {
  CommunicationDocumentsQuery,
  CommunicationDocumentsQueryVariables,
} from "generated-graphql/graphql";
import { useMemo } from "react";

export const COMMUNICATION_DOCUMENTS_QUERY = gql(`
  query CommunicationDocuments($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        title
        documentType
        storageLink
        fileExtension
        documentTags{
            id
            name
        }
        createdAt
        facilities {
          id
          name
          tierIIReports {
            id
            reportKind
            reportingYear
          }
        }
      }
      count
    }
  }
`);

function buildSearch(documentIds: string[], isUnpaginated: boolean) {
  if (isUnpaginated) {
    const sortedDocumentIds = [...documentIds].sort();
    return `id:${sortedDocumentIds.join("|")}`;
  }
  return `id:${documentIds.join("|")}`;
}

function reorderQueryData(documentIds: string[], data: CommunicationDocumentsQuery) {
  return {
    ...data,
    documents: {
      ...data.documents,
      items: documentIds
        .map((id) => data.documents.items.find((doc) => doc.id === id))
        .filter((doc): doc is NonNullable<typeof doc> => Boolean(doc)),
    },
  };
}

export function useCommunicationDocuments({
  documentIds,
  ...variables
}: {
  documentIds: string[];
} & CommunicationDocumentsQueryVariables) {
  // if there is no pagination data
  // we can order the documents by the documentIds order
  // to skip the query and then just sort in mem
  const isUnpaginated = !variables.page && !variables.pageSize;
  const search = useMemo(
    () => buildSearch(documentIds, isUnpaginated),
    [documentIds, isUnpaginated]
  );

  const queryResult = useQuery(COMMUNICATION_DOCUMENTS_QUERY, {
    variables: { ...variables, search },
    skip: !documentIds.length,
  });

  if (!isUnpaginated) {
    return queryResult;
  }

  return {
    ...queryResult,
    data: queryResult.data ? reorderQueryData(documentIds, queryResult.data) : undefined,
    previousData: queryResult.previousData
      ? reorderQueryData(documentIds, queryResult.previousData)
      : undefined,
  };
}
